import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import React from "react";
import { QRCodeCanvas } from "qrcode.react";
import { NavigateFunction } from "react-router-dom";
import { Layout } from "../SharedComponents/Layout";
import { logout } from "../SharedComponents/LoginGuard/loginGuard";
import { Pagination } from "../SharedComponents/Pagination";
import { SearchInput } from "../SharedComponents/SearchInput";
import { withNavigationHooks } from "../SharedComponents/withNavigationHooks";
import { calculateTotalPages } from "../utils/pagination";
import ArchiveApplicationModal, {
  ArchiveApplicationModalDelegate,
} from "./ArchiveApplicationModal/ArchiveApplicationModal";
import {
  IAssignment,
  ManagedAssignmentsController,
  ManagedAssignmentsControllerDelegate,
} from "./controller";
import CreateAssignmentModal, {
  CreateAssignmentModalDelegate,
} from "./CreateAssignmentModal/CreateAssignmentModal";
import ManagedAssignmentRow, {
  ManagedAssignmentRowDelegate,
} from "./ManagedAssignmentRow";
import ReimburseLicenseModal from "./ReimburseLicenseModal/ReimburseLicenseModal";
import UpdateDueDateModal from "./UpdateDueDateModal";
import { trackGtagEvent } from "../utils/analytics";

type AlertSeverity = "success" | "info" | "warning" | "error";

interface State {
  error: {};
  data: {
    assignments: IAssignment[];
    selectedArchiveApplicationId?: number;
    selectedReimburseApplicationId?: number;
    selectedDueDateApplicationId?: number;
  };
  openedCreateAssignmentModal: boolean;
  openedArchiveModal: boolean;
  openedReimburseModal: boolean;
  openedEditDueDateModal: boolean;
  isLoading: boolean;
  snackbar: {
    open: boolean;
    message: string;
    severity: AlertSeverity;
  };
  qrCodeModal: {
    open: boolean;
    link?: string;
    modalTitle?: string;
    assignmentTitle?: string;
    instrumentTitle?: string;
    alertMessage?: string;
    alertSeverity?: AlertSeverity;
  };
}

interface Props {
  navigationHooks: NavigateFunction;
}

class ManagedAssignments
  extends React.Component<Props, State>
  implements
  ManagedAssignmentsControllerDelegate,
  CreateAssignmentModalDelegate,
  ManagedAssignmentRowDelegate,
  ArchiveApplicationModalDelegate {
  private controller: ManagedAssignmentsController;
  private filters = {
    filter: "",
    page: 1,
    total: 1,
    limit: 10,
  };
  private readonly qrCanvasId = "managed-assignment-qr-canvas";

  constructor(props: Props) {
    super(props);
    this.controller = new ManagedAssignmentsController(this);
    this.state = {
      error: {},
      data: {
        assignments: [],
      },
      openedCreateAssignmentModal: false,
      openedArchiveModal: false,
      openedReimburseModal: false,
      openedEditDueDateModal: false,
      isLoading: true,
      snackbar: { open: false, message: "", severity: "success" },
      qrCodeModal: { open: false },
    };
  }
  pagination({ page, total }: { page: number; total: number }): void {
    throw new Error("Method not implemented.");
  }

  componentDidMount(): void {
    this.loadData();
  }

  // Delegate
  didGetAssignments(assignments: IAssignment[]): void {
    this.setState({
      isLoading: false,
      data: {
        ...this.state.data,
        assignments,
      },
    });
  }

  filter({
    page,
    total,
    filter,
  }: {
    page: number;
    total: number;
    filter: string;
  }): void {
    this.filters.page = page;
    this.filters.total = total;
    this.filters.filter = filter;
  }

  applicationShouldLogout(): void {
    logout();
  }

  genericError(): void { }

  didClickArchive(applicationId: number) {
    this.setState({
      data: {
        ...this.state.data,
        selectedArchiveApplicationId: applicationId,
      },
      openedArchiveModal: true,
    });
  }

  didClickCopyAssignment(applicationId: number) {
    const accessToken = localStorage.getItem("AccessToken")!;
    const promise = this.controller.getAssignmentToken(accessToken, applicationId);
    const assignment = this.getAssignmentDetails(applicationId);
    const snackbarMessage =
      "Tipo de link copiado: Identificado — Requer cadastro e login. Permite devolutiva individual.";
    const snackbarSeverity: AlertSeverity = "success";
    void promise.then((link) => {
      if (!link) {
        return;
      }
      this.trackApplicationShared(applicationId, "authenticated");
      this.openQrCodeModal({
        link,
        modalTitle: "QR Code do link identificado",
        assignmentTitle: assignment?.title,
        instrumentTitle: assignment?.instrumentTitle,
        alertMessage: snackbarMessage,
        alertSeverity: snackbarSeverity,
      });
    });
    this.setState({
      snackbar: {
        open: true,
        message: snackbarMessage,
        severity: snackbarSeverity,
      },
    });
  }

  didClickCopyAnonAssignment(applicationId: number) {
    const accessToken = localStorage.getItem("AccessToken")!;
    const promise = this.controller.getAnonAssignmentToken(accessToken, applicationId);
    const assignment = this.getAssignmentDetails(applicationId);
    const snackbarMessage =
      "Tipo de link copiado: Anônimo — Pode ser usado sem cadastro. Não permite devolutiva individual.";
    const snackbarSeverity: AlertSeverity = "warning";
    void promise.then((link) => {
      if (!link) {
        return;
      }
      this.trackApplicationShared(applicationId, "anonymous");
      this.openQrCodeModal({
        link,
        modalTitle: "QR Code do link anônimo",
        assignmentTitle: assignment?.title,
        instrumentTitle: assignment?.instrumentTitle,
        alertMessage: snackbarMessage,
        alertSeverity: snackbarSeverity,
      });
    });
    this.setState({
      snackbar: {
        open: true,
        message: snackbarMessage,
        severity: snackbarSeverity,
      },
    });
  }

  didClickCopyGenericAnonAssignment(applicationId: number) {
    const accessToken = localStorage.getItem("AccessToken")!;
    const promise = this.controller.getGenericAnonAssignmentToken(accessToken, applicationId);
    const assignment = this.getAssignmentDetails(applicationId);
    const snackbarMessage =
      "Tipo de link copiado: Anônimo — Pode ser usado sem cadastro. Não permite devolutiva individual.";
    const snackbarSeverity: AlertSeverity = "warning";
    void promise.then((link) => {
      if (!link) {
        return;
      }
      this.trackApplicationShared(applicationId, "anonymous");
      this.openQrCodeModal({
        link,
        modalTitle: "QR Code do link anônimo",
        assignmentTitle: assignment?.title,
        instrumentTitle: assignment?.instrumentTitle,
        alertMessage: snackbarMessage,
        alertSeverity: snackbarSeverity,
      });
    });
    this.setState({
      snackbar: {
        open: true,
        message: snackbarMessage,
        severity: snackbarSeverity,
      },
    });
  }

  private getAssignmentDetails(applicationId: number) {
    return this.state.data.assignments.find(
      (item) => item.applicationId === applicationId
    );
  }

  private openQrCodeModal({
    link,
    modalTitle,
    assignmentTitle,
    instrumentTitle,
    alertMessage,
    alertSeverity,
  }: {
    link: string;
    modalTitle: string;
    assignmentTitle?: string;
    instrumentTitle?: string;
    alertMessage?: string;
    alertSeverity?: AlertSeverity;
  }) {
    this.setState({
      qrCodeModal: {
        open: true,
        link,
        modalTitle,
        assignmentTitle,
        instrumentTitle,
        alertMessage,
        alertSeverity,
      },
    });
  }

  private closeQrCodeModal = () => {
    this.setState({
      qrCodeModal: { open: false },
    });
  };

  private handleSaveQrCode = () => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    const body = document.body;
    if (!body) {
      return;
    }

    body.classList.add("print-qr-only");

    const mediaQueryList =
      typeof window.matchMedia === "function"
        ? window.matchMedia("print")
        : undefined;
    let isCleaningUp = false;

    const removePrintClass = () => {
      if (isCleaningUp) {
        return;
      }
      isCleaningUp = true;
      body.classList.remove("print-qr-only");
      window.removeEventListener("afterprint", removePrintClass);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (mediaQueryList) {
        if (typeof mediaQueryList.removeEventListener === "function") {
          mediaQueryList.removeEventListener("change", handleMediaQueryChange);
        } else if (typeof mediaQueryList.removeListener === "function") {
          mediaQueryList.removeListener(handleMediaQueryChange);
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        removePrintClass();
      }
    };

    const handleMediaQueryChange = (event: MediaQueryListEvent) => {
      if (!event.matches) {
        removePrintClass();
      }
    };

    window.addEventListener("afterprint", removePrintClass);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    if (mediaQueryList) {
      if (typeof mediaQueryList.addEventListener === "function") {
        mediaQueryList.addEventListener("change", handleMediaQueryChange);
      } else if (typeof mediaQueryList.addListener === "function") {
        mediaQueryList.addListener(handleMediaQueryChange);
      }
    }

    window.print();
  };

  private handleCopyQrLink = () => {
    const { link } = this.state.qrCodeModal;
    if (!link) {
      return;
    }

    const fallbackCopy = () => {
      if (typeof document === "undefined") {
        throw new Error("Clipboard API indisponível");
      }
      const textarea = document.createElement("textarea");
      textarea.value = link;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const successful = document.execCommand("copy");
      document.body.removeChild(textarea);
      if (!successful) {
        throw new Error("Clipboard copy failed");
      }
    };

    const copyPromise =
      typeof navigator !== "undefined" &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function"
        ? navigator.clipboard.writeText(link)
        : new Promise<void>((resolve, reject) => {
          try {
            fallbackCopy();
            resolve();
          } catch (error) {
            reject(error);
          }
        });

    void copyPromise
      .then(() => {
        this.setState({
          snackbar: {
            open: true,
            message: "Link copiado para a área de transferência!",
            severity: "success",
          },
        });
      })
      .catch(() => {
        this.setState({
          snackbar: {
            open: true,
            message: "Não foi possível copiar o link.",
            severity: "error",
          },
        });
      });
  };

  didArchiveApplication() {
    this.setState(
      {
        data: {
          ...this.state.data,
          selectedArchiveApplicationId: undefined,
        },
        openedArchiveModal: false,
      },
      () => this.loadData()
    );
  }

  private trackApplicationShared(
    applicationId: number,
    shareType: "anonymous" | "authenticated"
  ) {
    if (
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      !document.cookie.includes("catvanteAnalyticsCookiesAccepted=true")
    ) {
      return;
    }

    const fbq = window.fbq;
    if (fbq) {
      fbq("trackCustom", "ApplicationShared", {
        application_id: String(applicationId),
        share_type: shareType,
      });
    }

    trackGtagEvent("application_shared", {
      application_id: String(applicationId),
      share_type: shareType,
    });
  }

  didReimburseLicenses() {
    this.setState(
      {
        openedReimburseModal: false,
        data: {
          ...this.state.data,
          selectedReimburseApplicationId: undefined,
        },
        snackbar: {
          open: true,
          message: "Licenças ressarcidas com sucesso!",
          severity: "success",
        },
      },
      () => this.loadData()
    );
  }

  didClickReimburse(applicationId: number) {
    this.setState({
      data: {
        ...this.state.data,
        selectedReimburseApplicationId: applicationId,
      },
      openedReimburseModal: true,
    });
  }

  didClickEditDueDate(applicationId: number) {
    const assignment = this.state.data.assignments.find(
      (item) => item.applicationId === applicationId
    );
    if (!assignment) {
      this.setState({
        snackbar: {
          open: true,
          message: "Não foi possível encontrar a aplicação selecionada.",
          severity: "error",
        },
      });
      return;
    }
    this.setState({
      data: {
        ...this.state.data,
        selectedDueDateApplicationId: applicationId,
      },
      openedEditDueDateModal: true,
    });
  }

  //Modals Delegate
  shouldCloseModal() {
    this.setState({
      openedArchiveModal: false,
      openedCreateAssignmentModal: false,
      openedReimburseModal: false,
      openedEditDueDateModal: false,
      data: {
        ...this.state.data,
        selectedArchiveApplicationId: undefined,
        selectedReimburseApplicationId: undefined,
        selectedDueDateApplicationId: undefined,
      },
    });
  }

  didCreateAssignment(
    _assignmentId: number,
    _payload: { name: string; instrumentId: number; dueDate: Date }
  ) {
    this.setState(
      {
        openedCreateAssignmentModal: false,
        isLoading: true,
      },
      () => {
        this.loadData();
      }
    );
  }

  // Internals
  didClickOpenAssignmentModal() {
    this.setState({
      openedCreateAssignmentModal: true,
    });
  }

  async updateAssignmentDueDate(nextDueDate: Date, applicationId?: number) {
    const targetApplicationId =
      applicationId || this.state.data.selectedDueDateApplicationId;
    if (!targetApplicationId) {
      throw new Error("Nenhuma aplicação selecionada para atualização.");
    }
    const accessToken = localStorage.getItem("AccessToken")!;
    try {
      await this.controller.updateAssignmentDueDate(
        accessToken,
        targetApplicationId,
        nextDueDate
      );
      this.setState(
        {
          openedEditDueDateModal: false,
          data: {
            ...this.state.data,
            selectedDueDateApplicationId: undefined,
          },
          snackbar: {
            open: true,
            message: "Prazo atualizado com sucesso!",
            severity: "success",
          },
          isLoading: true,
        },
        () => this.loadData()
      );
    } catch (error: any) {
      const message =
        error?.message ||
        (typeof error === "string"
          ? error
          : "Não foi possível atualizar o prazo.");
      throw new Error(message);
    }
  }

  loadData() {
    const token = localStorage.getItem("AccessToken")!;

    const newFilters: any = { ...this.filters };

    delete newFilters.total;

    Object.entries(newFilters).forEach(([key, value]) => {
      if (value === "") {
        delete newFilters[key];
      }
    });

    this.controller.getManagedAssignments(token, newFilters);
  }

  handlePageChange = (page: number) => {
    const totalPages =
      calculateTotalPages(this.filters.total, this.filters.limit) || 1;
    const nextPage = Math.min(Math.max(page, 1), totalPages);
    if (nextPage === this.filters.page) {
      return;
    }
    this.filters.page = nextPage;
    this.loadData();
  };

  renderLoading() {
    return (
      <Box flex={1} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  renderAssignmentRow() {
    return this.state.data.assignments.map((assignment, id) => (
      <ManagedAssignmentRow
        assignment={assignment}
        delegate={this}
        key={`user_row_${id}`}
      />
    ));
  }

  renderAssignmentTable() {
    return (
      <Box
        flex={1}
        overflow="auto"
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <Container
          maxWidth="lg"
          style={{
            padding: "80px 20px",
          }}
        >
          <Box
            marginBottom={2}
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                sm: 'row'
              },
              gap: '0.75rem',
              alignItems: {
                sm: 'center'
              },
              justifyContent: {
                xs: 'space-between',
              },
              flexWrap: 'wrap'
            }}
          >
            <Stack direction="column" spacing={1.25}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="h2">Aplicações</Typography>

                <Chip
                  label={`${this.filters?.total} ${this.filters?.total !== 1 ? "encontradas" : "encontrada"}`}
                  size="small"
                />
              </Box>

              <Typography variant="subtitle1" color="text.secondary">
                Gerencie e acompanhe as aplicações criadas
              </Typography>
            </Stack>

            <Box
              component={Button}
              variant="contained"
              flexWrap="wrap"
              onClick={() => this.didClickOpenAssignmentModal()}
            >
              Criar Aplicação
            </Box>
          </Box>

          <Box
            marginBottom={2}
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                sm: 'row'
              },
              gap: {
                xs: '1rem',
              },
              alignItems: {
                sm: 'center'
              },
              justifyContent: {
                xs: 'end',

              },
              flexWrap: 'wrap'
            }}
          >
            <SearchInput
              label="Pesquisar Aplicações"
              onChangeFilter={(filter) => {
                this.filters.filter = filter;
              }}
              onSearch={() => {
                this.filters.page = 1;
                this.loadData();
              }}
            />

          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell align="right">Data limite</TableCell>
                  <TableCell align="right">Instrumento</TableCell>
                  <TableCell align="right">Aplicações disponíveis</TableCell>
                  <TableCell align="right">Aplicações concluídas</TableCell>
                  <TableCell align="right">Aplicações não concluídas</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{this.renderAssignmentRow()}</TableBody>
            </Table>
          </TableContainer>

          <Pagination
            currentPage={this.filters.page}
            totalPages={calculateTotalPages(
              this.filters.total,
              this.filters.limit
            )}
            onChangePage={this.handlePageChange}
          />
        </Container>
      </Box>
    );
  }

  handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    this.setState({ snackbar: { ...this.state.snackbar, open: false } });
  };

  render() {
    const selectedDueDateAssignment = this.state.data.assignments.find(
      (assignment) =>
        assignment.applicationId ===
        this.state.data.selectedDueDateApplicationId
    );
    const { qrCodeModal } = this.state;
    return (
      <div
        style={{
          display: "flex",
          height: "100vh",
        }}
      >
        <Layout>
          {/* <Header logoutEnabled={true}/> */}
          {/* <Header2 /> */}
          {this.state.isLoading
            ? this.renderLoading()
            : this.renderAssignmentTable()}
          {this.state.openedCreateAssignmentModal && (
            <CreateAssignmentModal delegate={this} />
          )}
          {this.state.openedEditDueDateModal && selectedDueDateAssignment && (
            <UpdateDueDateModal
              key={selectedDueDateAssignment.applicationId}
              assignmentTitle={selectedDueDateAssignment.title}
              currentDueDate={selectedDueDateAssignment.dueDate}
              onClose={() => this.shouldCloseModal()}
              onSubmit={(date) =>
                this.updateAssignmentDueDate(
                  date,
                  selectedDueDateAssignment.applicationId
                )
              }
            />
          )}
          {this.state.openedArchiveModal && (
            <ArchiveApplicationModal
              delegate={this}
              applicationId={this.state.data.selectedArchiveApplicationId || 0}
            />
          )}

          {this.state.openedReimburseModal && (
            <ReimburseLicenseModal
              delegate={this}
              applicationId={
                this.state.data.selectedReimburseApplicationId || 0
              }
            />
          )}

          <Dialog
            open={qrCodeModal.open}
            onClose={this.closeQrCodeModal}
            maxWidth="xs"
            fullWidth
            variant="modal"
          >
            <DialogTitle variant="modal">
              <QrCode2Icon />
              {qrCodeModal.modalTitle || "QR Code do link"}
            </DialogTitle>
            <DialogContent
              className="qr-print-content"
              variant="modal"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              {qrCodeModal.alertMessage && (
                <Alert
                  severity={qrCodeModal.alertSeverity ?? "info"}
                >
                  {qrCodeModal.alertMessage}
                </Alert>
              )}
              {qrCodeModal.link && (
                <>
                  <Stack direction="column" spacing={0.75}>
                    {qrCodeModal.assignmentTitle && (
                      <Typography
                        variant="h5"
                        fontWeight={600}
                        align="center"
                        color="primary"
                      >
                        {qrCodeModal.assignmentTitle}
                      </Typography>
                    )}
                    {qrCodeModal.instrumentTitle && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                      >
                        Instrumento: {qrCodeModal.instrumentTitle}
                      </Typography>
                    )}
                  </Stack>
                  <QRCodeCanvas
                    value={qrCodeModal.link}
                    size={200}
                    id={this.qrCanvasId}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ wordBreak: "break-all" }}
                  >
                    {qrCodeModal.link}
                  </Typography>
                </>
              )}
            </DialogContent>
            <DialogActions className="qr-print-actions" variant="modal">
              <Box display={'flex'} gap={1} flexWrap={'wrap'} justifyContent={'end'}>
                <Button onClick={this.closeQrCodeModal} color="error" variant="outlined">
                  Fechar
                </Button>

                <Button
                  variant="outlined"
                  onClick={this.handleSaveQrCode}
                  disabled={!qrCodeModal.link}
                >
                  Salvar QR code
                </Button>

                <Button
                  variant="contained"
                  onClick={this.handleCopyQrLink}
                  disabled={!qrCodeModal.link}
                >
                  Copiar link
                </Button>
              </Box>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={this.state.snackbar.open}
            autoHideDuration={6000}
            onClose={this.handleCloseSnackbar}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert
              onClose={this.handleCloseSnackbar}
              severity={this.state.snackbar.severity}
              sx={{ width: "100%" }}
            >
              {this.state.snackbar.message}
            </Alert>
          </Snackbar>
        </Layout>
      </div>
    );
  }
}

export default withNavigationHooks(ManagedAssignments);
